import { useEffect, useState } from "react";
import { bearingDeg, distanceNm } from "@radar-sim/engine";
import type { RadarStateSource } from "@radar-sim/engine";
import type { WorldSnapshot } from "@radar-sim/schema";

/**
 * MOB recovery readout. Stands alone in the right column rather than
 * inside the Helm card so it can pop into existence without pushing
 * the helm controls (course, speed, simulator-speed presets, pause)
 * off the bottom of the visible area. Hidden when no SART is active.
 *
 * Layout is horizontal: BRG / RNG / ELAPSED side-by-side, then a
 * "Steer to MOB" button below. Compact enough that even with the
 * chart panel open, the top of the right column still shows targets,
 * recovery, and helm together.
 */
export function SartRecoveryPanel(props: {
  source: RadarStateSource;
  onSetCourse: (deg: number) => void;
}): JSX.Element | null {
  const [snapshot, setSnapshot] = useState<WorldSnapshot | null>(null);
  useEffect(() => {
    const stop = props.source.subscribe((s) => setSnapshot(s));
    return stop;
  }, [props.source]);

  if (snapshot === null || snapshot.sarts.length === 0) return null;

  // Most-recently activated SART wins — typical case is one SART per
  // scenario, but if a scenario drops several we always recover to
  // the freshest call.
  const sart = [...snapshot.sarts].sort(
    (a, b) => b.activatedAtSimSeconds - a.activatedAtSimSeconds,
  )[0]!;
  const range = distanceNm(snapshot.ownShip.position, sart.position);
  const bearing = bearingDeg(snapshot.ownShip.position, sart.position);
  const elapsedSec = Math.max(
    0,
    snapshot.simTimeSeconds - sart.activatedAtSimSeconds,
  );
  const mm = Math.floor(elapsedSec / 60);
  const ss = Math.floor(elapsedSec % 60);

  return (
    <div
      role="region"
      aria-label="Man overboard recovery"
      style={{
        background: "rgba(239, 68, 68, 0.08)",
        border: "1px solid var(--danger)",
        borderRadius: "var(--radius-md)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <strong
          style={{
            color: "var(--danger)",
            fontSize: 12,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          MOB · Recovery
        </strong>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--text-muted)",
          }}
        >
          {mm}:{ss.toString().padStart(2, "0")}
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: "var(--text)",
        }}
      >
        <Stat label="BRG" value={`${bearing.toFixed(0).padStart(3, "0")}°`} />
        <Stat label="RNG" value={`${range.toFixed(2)} nm`} />
      </div>
      <button
        onClick={() => props.onSetCourse(bearing)}
        aria-label="Steer to SART"
        style={{
          background: "var(--danger)",
          color: "white",
          border: "1px solid var(--danger)",
          borderRadius: "var(--radius-sm)",
          padding: "8px 12px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Steer to MOB ({bearing.toFixed(0).padStart(3, "0")}°)
      </button>
    </div>
  );
}

function Stat(props: { label: string; value: string }): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span style={{ fontSize: 10, color: "var(--text-dim)" }}>{props.label}</span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{props.value}</span>
    </div>
  );
}
